// @flow
import * as React from 'react';
import {Component} from 'react';
import PropTypes from 'prop-types';

import {evaluationIsVariant, evaluationIsOn, logExperiment} from '../../utils';
import ExperimentContext from './ExperimentContext';

import type {AbacusEvaluationData, AbacusExperimentValue} from '../../types';
import type {ExperimentProviderContextValue} from './ExperimentProvider';

export const ON = 'on';
export const OFF = 'off';

type ExperimentVariant = 'on' | 'off' | AbacusExperimentValue;

/**
 * Args exposed via renderProp function when using Experiment components with
 * function children:
 *  <Experiment name="MY_TEST" variant={1}>
 *      {({bucketConfig}) => <p>{bucketConfig}</p>}
 *  </Experiment>
 */
type ExperimentVariantRenderProps = {
    bucketConfig: $PropertyType<
        $PropertyType<AbacusEvaluationData, 'bucket'>,
        'bucketConfig'
    >,
    logExperiment: Function
};

type ExperimentVariantChildren =
    | React.Node
    | ((ExperimentVariantRenderProps) => React.Node);

type YieldedExperimentComponentProps = {children?: ExperimentVariantChildren};
type YieldedVariantComponentProps = {
    children?: ExperimentVariantChildren,
    variant: ExperimentVariant
};

type YieldedExperimentComponent = React.ComponentType<
    YieldedExperimentComponentProps
>;
type YieldedVariantComponent = React.ComponentType<
    YieldedVariantComponentProps
>;

type ExperimentRenderProps = {
    On: YieldedExperimentComponent,
    Off: YieldedExperimentComponent,
    Variant: YieldedVariantComponent
} & ExperimentVariantRenderProps;

type ExperimentChildren = React.Node | ((ExperimentRenderProps) => React.Node);

type WithExperimentProps = {
    abacus: ExperimentProviderContextValue,
    children?: ExperimentChildren,
    deferLogging: boolean,
    name: string,
    variant?: ExperimentVariant
};

/**
 * Coerces `undefined` children value to `null` for safe use as the return value
 * of a component's render function.
 * @param {React.Node} children
 */
function safeReturnChildren(children?: React.Node) {
    return typeof children === 'undefined' ? null : children;
}

class WithExperiment extends Component<WithExperimentProps> {
    constructor(...args) {
        super(...args);
        this.On.displayName = 'On';
        this.Off.displayName = 'Off';
        this.Variant.displayName = 'Variant';
    }

    componentDidMount() {
        if (!this.props.deferLogging && this.evaluationData) {
            logExperiment(this.evaluationData);
        }
    }

    get evaluationData() {
        const {abacus, name} = this.props;
        return abacus[name];
    }

    /**
     * Property initializers for functions passed down via renderProp children interface:
     *
     * - deferredExperimentLogger
     * - On component
     * - Off component
     * - Variant component
     */
    deferredExperimentLogger = () => {
        if (this.props.deferLogging && this.evaluationData) {
            return logExperiment(this.evaluationData);
        }

        return (
            !this.props.deferLogging &&
            // eslint-disable-next-line no-console
            console.warn(
                `Warning: called yielded logExperiment function for experiment ${
                    this.props.name
                }, but experiment was already logged during componentDidMount. To defer logging, add the "deferLogging" prop to your <Experiment> component.`
            )
        );
    };

    On = ({children}) => {
        this.throwIfHasVariantProp();
        return this.experimentIsVariant(ON)
            ? safeReturnChildren(children)
            : null;
    };

    Off = ({children}) => {
        this.throwIfHasVariantProp();
        return this.experimentIsVariant(OFF)
            ? safeReturnChildren(children)
            : null;
    };

    Variant = ({children, variant}) => {
        this.throwIfHasVariantProp();
        return this.experimentIsVariant(variant)
            ? safeReturnChildren(children)
            : null;
    };

    /**
     * Instance methods
     */
    throwIfHasVariantProp() {
        if (
            this.props.variant !== null &&
            typeof this.props.variant !== 'undefined'
        ) {
            const variant = Array.isArray(this.props.variant)
                ? `[${this.props.variant.join(',')}]`
                : this.props.variant;

            throw new Error(
                `Used On, Off, or Variant components with <Experiment name="${
                    this.props.name
                }" variant={${variant}}/>. The variant prop overrides yielded components and not all branches may be reachable. For best results, remove the variant prop if you want to use the yielded On, Off, or Variant components.`
            );
        }
    }

    experimentIsVariant(variant) {
        const {evaluationData} = this;

        if (variant === 'on') {
            return evaluationIsOn(evaluationData);
        }

        if (variant === 'off') {
            return evaluationIsVariant(evaluationData, 0);
        }

        return evaluationIsVariant(evaluationData, variant);
    }

    hasVariantProp() {
        return (
            this.props.variant !== null &&
            typeof this.props.variant !== 'undefined'
        );
    }

    requireChildrenFunctionIfNoVariantProp() {
        throw new Error(
            `A children function is required when using <Experiment name="${
                this.props.name
            }"> without a "variant" prop. Either specify a variant, or pass a child function and use the supplied On, Off, or Variant components.
Example:
<Experiment name="MY_TEST">
    {({On}) => (
        <p>The test is on!</p>
    )}
</Experiment>`
        );
    }

    render() {
        const {children, variant} = this.props;
        const evaluationData = this.evaluationData || {bucket: {}};
        const {
            bucket: {bucketConfig}
        } = evaluationData;
        const {On, Off, Variant, deferredExperimentLogger} = this;

        if (!this.hasVariantProp() && typeof children !== 'function') {
            this.requireChildrenFunctionIfNoVariantProp();
        }

        if (typeof children === 'function') {
            return children({
                bucketConfig,
                logExperiment: deferredExperimentLogger,
                On,
                Off,
                Variant
            });
        }

        if (!this.experimentIsVariant(variant)) {
            return null;
        }

        return safeReturnChildren(children);
    }
}

type ExperimentProps = {
    children?: ExperimentChildren,
    deferLogging: boolean,
    name: string,
    variant?: ExperimentVariant
};

const ExperimentPropTypes = {
    /** Nodes to render if bucketed into the requested experiment and variant,
     * or function. If a function, will be invoked with On, Off, and Variant
     * components, a logExperiment function and the experiment's
     * bucketConfig, if present. */
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
    /** Flag to defer logging an exposure on `componentDidMount`. To be used
     * when test exposure might be gated by a user action or when an element
     * scrolls into view. */
    deferLogging: PropTypes.bool.isRequired,
    /** The name of the experiment. */
    name: PropTypes.string.isRequired,
    /** If not passing a children function, sets the variant in which the
     * component's children should be rendered. */
    variant: PropTypes.oneOfType([
        PropTypes.oneOf(['on', 'off']),
        PropTypes.number,
        PropTypes.arrayOf(PropTypes.number)
    ])
};

/**
 * Default Experiment component. In its most basic form, renders the child content
 * if bucketed into the passed experiment name in the given variant:
 *
 * ```jsx
 *  <Experiment name="MY_EXPERIMENT" variant={1}>
 *      Children render if MY_EXPERIMENT is active and we're bucketed into variant 1.
 *      An exposure is logged.
 *  </Experiment>
 * ```
 *
 * Alternately, pass 'on' or 'off' if it doesn't matter what variant is active:
 *
 * ```jsx
 * <Experiment name="MY_EXPERIMENT" variant={'on'}>
 * <Experiment name="MY_EXPERIMENT" variant={Experiment.ON}>
 *
 * <Experiment name="MY_EXPERIMENT" variant={'off'}>
 * <Experiment name="MY_EXPERIMENT" variant={Experiment.OFF}>
 * <Experiment name="MY_EXPERIMENT" variant={0}> // renders in both control and inactive states
 * ```
 *
 * Pass an array to render if any of the supplied variants is active:
 *
 * ```jsx
 * <Experiment name="MY_EXPERIMENT" variant={[1, 3]}>
 * ```
 *
 * Pass a child function to recieve:
 * - `bucketConfig` for the test (string data configured in the TNL dashboard)
 * - `logExperiment` for manually logging experiment in response to user action
 *      - requires `deferLogging` prop on parent component
 *      - logs a warning if `deferLogging` is missing
 *
 * ```jsx
 * <Experiment name="MY_EXPERIMENT" deferLogging>
 *      {({On, Off, bucketConfig: insultingAdjective, logExperiment}) => (
 *          <On>
 *              <MessageShower onClick={logExperiment} label={'Click me'}>
 *                  <p>You have an {insultingAdjective} face</p>
 *              </MessageShower>
 *          </On>
 *          <Off>
 *              <MessageShower onClick={logExperiment} label={'Click me'}>
 *                  <p>Gosh thanks for clicking</p>
 *              </MessageShower>
 *          </Off>
 *      )}
 * </Experiment>
 * ```
 *
 * A warning will be logged if you call the yielded `logExperiment` but forget to
 * set `deferLogging` on the Experiment component:
 *
 * ```jsx
 * <Experiment name="MY_EXPERIMENT" variant={Experiment.ON}>
 *      {({logExperiment}) => (
 *          <button onClick={logExperiment}>Click me</button> // <-- logs a console warning when the button is clicked
 *      )}
 * </Experiment>
 * ```
 *
 * Unlike regular children-rendering components, Experiment won't throw a
 * "nothing returned from render" error if its children evaluate to `undefined`.
 * This is because in normal usage, <Experiment> is wrapped around a chunk of
 * another component's render that wouldn't normally be subject to the "must
 * not return undefined" requirement.
 *
 * That means the following is allowed even when `condition` is `undefined`:
 * <Experiment name="MY_EXPERIMENT" variant={OFF}>
 *     {condition && (
 *         <p>Some content</p>
 *     )}
 * </Experiment>
 *
 * When passed a child function, Experiment will also yield On, Off, and Variant
 * components, bound to the specified test name, to help express your test more concisely.
 * Wrap in a <React.Fragment> to render multiple components side-by-side:
 *
 * ```jsx
 *  <Experiment name="MY_TEST">
 *      {({On, Off}) => (
 *          <React.Fragment>
 *              <On>
 *                  <p>Hey check out this new component!</p>
 *              </On>
 *              <Off>
 *                  <p>Old, boring, musty component</p>
 *              </Off>
 *          </React.Fragment>
 *      )}
 *  </Experiment>
 * ```
 *
 * Usage w/ variants, including multi-variant matching:
 *  <Experiment name="MY_TEST">
 *      {({Variant, Off}) => (
 *          <React.Fragment>
 *              <Variant variant={1}>
 *                  <p>Hey check out this new component!</p>
 *              </Variant>
 *              <Variant variant={[1,2]}>
 *                  <p>I'll render in both variant 1 and variant 2!
 *              </Variant>
 *              <Off>
 *                  <p>Old, boring, musty component</p>
 *              </Off>
 *          </React.Fragment>
 *      )}
 *  </Experiment>
 *
 * Usage w/ renderProps to access bucketConfig:
 *  <Experiment name="MY_TEST">
 *      {({On, Off, bucketConfig}) => (
 *          <React.Fragment>
 *              <On>
 *                  <p>Hey check out this new {bucketConfig} component!</p>
 *              </On>
 *              <Off>
 *                  <p>Old, boring, musty component</p>
 *              </Off>
 *          </React.Fragment>
 *      )}
 *  </Experiment>
 *
 * An error will be thrown if you try to use one of the yielded components alongside
 * a top-level `variant` prop on the Experiment component itself.
 */
const Experiment = ({
    children,
    deferLogging,
    name,
    variant
}: ExperimentProps) => {
    return (
        <ExperimentContext.Consumer>
            {(abacus) => (
                <WithExperiment
                    abacus={abacus}
                    deferLogging={deferLogging}
                    name={name}
                    variant={variant}
                >
                    {children}
                </WithExperiment>
            )}
        </ExperimentContext.Consumer>
    );
};
Experiment.propTypes = ExperimentPropTypes;
Experiment.defaultProps = {
    deferLogging: false
};
Experiment.displayName = 'Experiment';
Experiment.ON = ON;
Experiment.OFF = OFF;

export default Experiment;
